
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Menu, MenuItem } from '@/types/menu';
import { toast } from 'sonner';
import { processMenuData, checkHappyHour } from '@/utils/menuDataProcessor';

export interface UseMenuDataResult {
  menu: Menu | null;
  items: MenuItem[];
  loading: boolean;
  error: string | null;
  isHappyHour: boolean;
}

export function useMenuData(slug: string | undefined): UseMenuDataResult {
  const [menu, setMenu] = useState<Menu | null>(null);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isHappyHour, setIsHappyHour] = useState(false);
  const [hasTrackedView, setHasTrackedView] = useState(false);

  useEffect(() => {
    if (!slug) {
      setError('No menu slug provided');
      setLoading(false);
      return;
    }

    const fetchMenuAndItems = async () => {
      try {
        await fetchMenu(slug);
      } catch (error: any) {
        setError('Failed to load menu. Please try again later.');
        toast.error('Erreur lors du chargement du menu');
        setLoading(false);
      }
    };

    fetchMenuAndItems();
  }, [slug, hasTrackedView]);

  const fetchMenu = async (slug: string) => {
    try {
      // Récupération du menu seul d'abord
      const { data: menuData, error: menuError } = await supabase
        .from('menus')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (menuError) {
        throw menuError;
      }
      
      if (!menuData) {
        setError('Menu not found');
        setLoading(false);
        return;
      }
      
      // Récupération des données restaurant si restaurant_id existe
      let restaurantData = null;
      if (menuData.restaurant_id) {
        const { data: restaurant, error: restaurantError } = await supabase
          .from('restaurants')
          .select('banner_url, name, phone')
          .eq('id', menuData.restaurant_id)
          .maybeSingle();
        
        if (!restaurantError && restaurant) {
          restaurantData = restaurant;
        }
      }
      
      // Enrichissement des données menu avec les infos restaurant
      const customCss = menuData.custom_css as Record<string, any> | null;
      const enrichedMenuData = {
        ...menuData,
        banner_url: customCss?.bannerUrl || restaurantData?.banner_url,
        phone: restaurantData?.phone,
        restaurant_name: restaurantData?.name,
      };
      
      const processedMenu = processMenuData(enrichedMenuData);
      setMenu(processedMenu);
      
      if (processedMenu.custom_css?.happyHourSchedule) {
        const isActive = checkHappyHour(processedMenu.custom_css.happyHourSchedule);
        setIsHappyHour(isActive);
      }

      await fetchMenuItems(menuData.id);
      await trackMenuView(menuData);
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuItems = async (menuId: string) => {
    try {
      const { data: itemsData, error: itemsError } = await supabase
        .from('menu_items')
        .select('*')
        .eq('menu_id', menuId)
        .order('order_position', { ascending: true });

      if (itemsError) {
        throw itemsError;
      }
      
      const processedItems = (itemsData || []).map(item => ({
        ...item,
        allergens: item.allergens || []
      }));
      
      setItems(processedItems);
    } catch (error) {
      // Silent error handling
    }
  };

  const trackMenuView = async (menuData: any) => {
    // Track menu view - only once per session
    if (!hasTrackedView && menuData.restaurant_id) {
      const today = new Date().toISOString().split('T')[0];
      
      try {
        await supabase.rpc('increment_menu_view', {
          p_menu_id: menuData.id,
          p_restaurant_id: menuData.restaurant_id,
          p_date: today
        });
        setHasTrackedView(true);
      } catch (viewError) {
        // Continue with the rest of the process even if view tracking fails
      }
    }
  };

  return { menu, items, loading, error, isHappyHour };
}
